import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './FloorPlanTable.css';

export const getTableScale = (seats = 4) => Math.min(1.6, Math.max(0.7, 1 + (seats - 4) * 0.1));

function FloorPlanTable({ table, onDragEnd, onContextMenu, isSelected, selectionMode, onSelect }) {
  const { t } = useTranslation();
  const isPointerDown = useRef(false);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const getShapeClass = () => {
    switch (table.shape) {
      case 'round':
        return 'fp-table--round';
      case 'rect':
        return 'fp-table--rect';
      default:
        return 'fp-table--square';
    }
  };

  const getStatusClass = () => {
    switch (table.status) {
      case 'occupied':
        return 'fp-table--occupied';
      case 'reserved':
        return 'fp-table--reserved';
      case 'maintenance':
        return 'fp-table--maintenance';
      default:
        return 'fp-table--available';
    }
  };

  const handlePointerDown = useCallback((e) => {
    if (e.button === 2) return; // ignore right click
    isPointerDown.current = true;
    isDragging.current = false;
    startPos.current = { x: e.clientX, y: e.clientY };
    e.target.closest('.fp-table').setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!isPointerDown.current) return;

    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);
    if (dx > 3 || dy > 3) {
      isDragging.current = true;
    }

    if (!isDragging.current) return;

    const viewport = e.target.closest('.fp-canvas-viewport');
    if (!viewport) return;

    const rect = viewport.getBoundingClientRect();
    let pctX = ((e.clientX - rect.left) / rect.width) * 100;
    let pctY = ((e.clientY - rect.top) / rect.height) * 100;

    // Clamp
    pctX = Math.max(2, Math.min(98, pctX));
    pctY = Math.max(2, Math.min(98, pctY));

    const el = e.target.closest('.fp-table');
    if (el) {
      el.style.left = `${pctX}%`;
      el.style.top = `${pctY}%`;
    }
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      isPointerDown.current = false;

      // Release pointer capture explicitly
      const el = e.target.closest('.fp-table');
      if (el && el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }

      if (!isDragging.current) {
        // In selection mode, toggle selection instead of context menu
        if (selectionMode && onSelect) {
          onSelect(table.tableNumber);
          return;
        }
        // It was a click, not a drag — show context menu
        if (onContextMenu) {
          onContextMenu(table, e);
        }
        return;
      }
      isDragging.current = false;

      const viewport = el?.closest('.fp-canvas-viewport');
      if (!viewport) return;

      const rect = viewport.getBoundingClientRect();
      let pctX = ((e.clientX - rect.left) / rect.width) * 100;
      let pctY = ((e.clientY - rect.top) / rect.height) * 100;

      pctX = Math.max(2, Math.min(98, pctX));
      pctY = Math.max(2, Math.min(98, pctY));

      onDragEnd(table.tableNumber, pctX, pctY);
    },
    [onDragEnd, onContextMenu, table, selectionMode, onSelect]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (selectionMode && onSelect) {
          onSelect(table.tableNumber);
        } else if (onContextMenu) {
          onContextMenu(table, e);
        }
      }
    },
    [onContextMenu, table, selectionMode, onSelect]
  );

  const scale = getTableScale(table.seats);
  const tableLabel = `${t('tables.table')} ${table.tableNumber} — ${table.seats} ${t('tables.seatsLabel')}, ${t(`tables.${table.status || 'available'}`)}`;

  return (
    <div
      ref={containerRef}
      className={`fp-table ${getShapeClass()} ${getStatusClass()}${isSelected ? ' fp-table--selected' : ''}`}
      style={{
        left: `${table.positionX}%`,
        top: `${table.positionY}%`,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
      tabIndex={0}
      role="button"
      aria-label={tableLabel}
      aria-pressed={selectionMode ? isSelected : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      onContextMenu={(e) => {
        e.preventDefault();
        if (onContextMenu) onContextMenu(table, e);
      }}
      title={`${t('tables.table')} ${table.tableNumber} — ${table.seats} ${t('tables.seatsLabel')}`}
    >
      <span className="fp-table-number">{table.tableNumber}</span>
      <span className="fp-table-seats">{t('tables.seatsShort', { count: table.seats })}</span>
    </div>
  );
}

export default FloorPlanTable;
